/**
 * 用途：广告平台 OAuth 授权回调处理页，接收授权码并完成账号绑定，然后跳回来源页
 * 路由：/oauth/callback
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { accountsApi } from '../api/accounts';
import type { OAuthCallbackResponse } from '../api/accounts';

type CallbackStatus = 'loading' | 'success' | 'error';
type OAuthPlatform = 'facebook' | 'google' | 'tiktok' | 'unknown';

const inflightCallbacks = new Map<string, Promise<OAuthCallbackResponse | undefined>>();

function callbackKey(code?: string, state?: string): string {
  return `${code || ''}::${state || ''}`;
}

function decodeOAuthPlatformFromState(state?: string): OAuthPlatform {
  if (!state) return 'unknown';
  try {
    const payload = state.split('.')[0] || '';
    if (!payload) return 'unknown';
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(window.atob(padded)) as { platform?: string };
    const p = String(decoded?.platform || '').toLowerCase();
    if (p === 'facebook' || p === 'google' || p === 'tiktok') {
      return p;
    }
  } catch {
    // ignore malformed state
  }
  return 'unknown';
}

function platformLabel(platform: OAuthPlatform): string {
  switch (platform) {
    case 'facebook':
      return 'Facebook';
    case 'google':
      return 'Google';
    case 'tiktok':
      return 'TikTok';
    default:
      return '平台';
  }
}

async function runCallbackOnce(params: {
  code?: string;
  state?: string;
  error?: string;
  error_reason?: string;
}): Promise<OAuthCallbackResponse | undefined> {
  const key = callbackKey(params.code, params.state);
  const doneKey = `oauth_callback_done:${key}`;
  if (typeof window !== 'undefined' && sessionStorage.getItem(doneKey) === '1') {
    return;
  }

  const existing = inflightCallbacks.get(key);
  if (existing) {
    return existing;
  }

  const task = (async () => {
    const resp = await accountsApi.oauthCallback(params);
    return resp;
  })();

  inflightCallbacks.set(key, task);
  try {
    return await task;
  } finally {
    inflightCallbacks.delete(key);
  }
}

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('正在处理授权回调...');
  const [selectionToken, setSelectionToken] = useState('');
  const [accounts, setAccounts] = useState<Array<{ account_id: string; account_name: string }>>([]);
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const params = useMemo(
    () => ({
      code: searchParams.get('code') ?? undefined,
      state: searchParams.get('state') ?? undefined,
      error: searchParams.get('error') ?? undefined,
      error_reason: searchParams.get('error_reason') ?? undefined,
    }),
    [searchParams]
  );

  const oauthPlatform = useMemo(() => decodeOAuthPlatformFromState(params.state), [params.state]);
  const oauthPlatformLabel = useMemo(() => platformLabel(oauthPlatform), [oauthPlatform]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!params.code && !params.error) {
        setStatus('error');
        setMessage('缺少回调参数，无法完成绑定');
        return;
      }
      try {
        const resp = await runCallbackOnce(params);
        if (!mounted) {
          return;
        }
        if (resp?.requires_selection && resp.selection_token && Array.isArray(resp.accounts) && resp.accounts.length > 0) {
          setSelectionToken(resp.selection_token);
          setAccounts(resp.accounts);
          setSelectedIDs([]);
          setStatus('success');
          setMessage(
            resp.accounts.length === 1
              ? `检测到 ${oauthPlatformLabel} 广告账号，请确认绑定。`
              : `检测到 ${resp.accounts.length} 个 ${oauthPlatformLabel} 广告账号，请选择后确认绑定。`
          );
          return;
        }
        setStatus('success');
        setMessage(resp?.message || `${oauthPlatformLabel} 账号绑定成功，正在返回账号列表...`);
        if (typeof window !== 'undefined') {
          const key = callbackKey(params.code, params.state);
          sessionStorage.setItem(`oauth_callback_done:${key}`, '1');
        }
        setTimeout(() => {
          navigate('/auth-accounts', { replace: true });
        }, 1200);
      } catch (err) {
        if (!mounted) {
          return;
        }
        setStatus('error');
        setMessage((err as Error).message || 'OAuth 回调处理失败');
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [navigate, oauthPlatformLabel, params]);

  const color = status === 'success' ? 'var(--green)' : status === 'error' ? 'var(--red)' : 'var(--primary)';

  const onConfirmSelection = async () => {
    if (!selectionToken || submitting) {
      return;
    }
    if (selectedIDs.length === 0) {
      setStatus('error');
      setMessage('请至少选择一个广告账号');
      return;
    }
    try {
      setSubmitting(true);
      const resp = await accountsApi.selectFacebookAccount({
        selection_token: selectionToken,
        account_ids: selectedIDs,
      });
      if (typeof window !== 'undefined') {
        const key = callbackKey(params.code, params.state);
        sessionStorage.setItem(`oauth_callback_done:${key}`, '1');
      }
      setStatus('success');
      setMessage(resp.message || '账号绑定成功。');
      setTimeout(() => {
        navigate('/auth-accounts', { replace: true });
      }, 1800);
    } catch (err) {
      setStatus('error');
      setMessage((err as Error).message || '选择账号并绑定失败');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAccount = (accountID: string) => {
    setSelectedIDs((prev) => {
      if (prev.includes(accountID)) {
        return prev.filter((id) => id !== accountID);
      }
      return [...prev, accountID];
    });
  };

  return (
    <div className="page-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
      <div className="card" style={{ width: 520, maxWidth: '92vw', padding: 28 }}>
        <h2 style={{ margin: 0, marginBottom: 10 }}>{oauthPlatformLabel} OAuth 回调</h2>
        <p style={{ margin: 0, color: 'var(--text3)', marginBottom: 20 }}>正在完成账号绑定，请稍候。</p>
        <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--panel2)', borderLeft: `3px solid ${color}`, color: 'var(--text2)' }}>
          {message}
        </div>
        {accounts.length > 0 && status !== 'error' && (
          <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
            {accounts.map((item) => (
              <label
                key={item.account_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  background: 'var(--panel2)',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIDs.includes(item.account_id)}
                  onChange={() => toggleAccount(item.account_id)}
                  disabled={submitting}
                />
                <span style={{ color: 'var(--text2)' }}>
                  {item.account_name} ({item.account_id})
                </span>
              </label>
            ))}
            <button className="btn btn-primary" disabled={submitting || selectedIDs.length === 0} onClick={() => void onConfirmSelection()}>
              {submitting ? '绑定中...' : `确认绑定（${selectedIDs.length}）`}
            </button>
          </div>
        )}
        {status === 'error' && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => navigate('/auth-accounts', { replace: true })}>
              返回授权管理
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
